import { motion } from "framer-motion";
import { ArrowLeft, Compass, Home, Mail } from "lucide-react";
import { Link } from "react-router-dom";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } };

export default function NotFound() {
  return (
    <main
      data-testid="not-found-page"
      className="relative min-h-screen overflow-hidden px-5 pb-20 pt-32 lg:px-8"
      style={{ background: "linear-gradient(180deg, #F8FAFC 0%, #EEF6FA 45%, #FFFFFF 100%)" }}
    >
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -left-24 top-20 h-72 w-72 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(43,135,190,0.18) 0%, rgba(43,135,190,0) 70%)" }}
        />
        <div
          className="absolute -right-16 bottom-10 h-80 w-80 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(13,58,79,0.16) 0%, rgba(13,58,79,0) 70%)" }}
        />
      </div>

      <div className="relative mx-auto flex min-h-[70vh] max-w-6xl items-center">
        <div className="grid w-full grid-cols-1 gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14">
          <motion.section
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.6 }}
            variants={fadeUp}
            className="rounded-[2rem] border border-white/70 bg-white/80 p-8 shadow-[0_24px_80px_rgba(13,58,79,0.08)] backdrop-blur md:p-12"
          >
            <div className="section-label mb-5">Error 404</div>
            <div className="mb-6 inline-flex items-center gap-3 rounded-full border border-[#D5E8F1] bg-[#EBF5FB] px-4 py-2 text-sm font-semibold text-[#1B5F7E]">
              <Compass size={16} />
              La ruta solicitada no existe
            </div>
            <h1
              className="mb-5 text-5xl font-semibold leading-tight md:text-6xl"
              style={{ color: "#0D3A4F", fontFamily: "Playfair Display, serif" }}
            >
              Esta página
              <br />
              se ha perdido
              <em style={{ color: "#1B5F7E" }}> fuera del mapa.</em>
            </h1>
            <p
              className="max-w-2xl text-base leading-8 text-slate-600 md:text-lg"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              La URL que intentabas abrir no corresponde a ninguna página activa de DAC-PAC.
              Puedes volver al inicio o ir a una sección útil desde aquí.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/" className="btn-primary">
                <Home size={16} />
                Ir al inicio
              </Link>
              <Link to="/contacto" className="btn-outline">
                <Mail size={16} />
                Contacto
              </Link>
            </div>
          </motion.section>

          <motion.aside
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.65, delay: 0.08 }}
            className="flex flex-col justify-between rounded-[2rem] border border-[#D9E7EF] bg-[#0D3A4F] p-8 text-white shadow-[0_20px_70px_rgba(13,58,79,0.22)] md:p-10"
          >
            <div>
              <div className="mb-3 text-sm font-semibold uppercase tracking-[0.24em] text-[#B8D9E8]">
                Siguientes pasos
              </div>
              <div className="mb-8 text-7xl font-semibold leading-none" style={{ fontFamily: "Playfair Display, serif" }}>
                404
              </div>
              <p className="max-w-sm text-sm leading-7 text-slate-200" style={{ fontFamily: "Manrope, sans-serif" }}>
                Si has llegado desde un enlace antiguo, probablemente la dirección haya cambiado o ya no esté disponible.
              </p>
            </div>

            <div className="space-y-4">
              <Link
                to="/campamento-verano"
                className="block rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-sm transition hover:bg-white/10"
              >
                <span className="mb-1 block font-semibold text-white">Campamento de verano</span>
                <span className="text-slate-300">Consulta la información y solicita aviso de la próxima edición.</span>
              </Link>
              <Link
                to="/faq"
                className="block rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-sm transition hover:bg-white/10"
              >
                <span className="mb-1 block font-semibold text-white">Preguntas frecuentes</span>
                <span className="text-slate-300">Revisa dudas habituales sobre inscripciones y funcionamiento.</span>
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#B8D9E8] transition hover:text-white"
              >
                <ArrowLeft size={15} />
                Volver a una ruta válida
              </Link>
            </div>
          </motion.aside>
        </div>
      </div>
    </main>
  );
}
